// §6 build orchestration: files + meta -> deterministic MASL + root CID. Pure and credential-free
// (no clock, no RNG, no network, no filesystem). Identical input -> identical rootCid (§3.7, §15).
import type { BuildMeta, BuildResult, BuiltBlob, FileInput } from "./types.ts";
import { normalize, BuildError } from "./normalize.ts";
import { synthesize } from "./synthesize.ts";
import { contentTypeFor } from "./content-type.ts";
import { canonicalBlobMimeType } from "./sniff.ts";
import { assembleMasl } from "./masl.ts";
import { canonicalizeRouting } from "./routing.ts";
import { rawCid, dagCbor } from "./cid.ts";

export async function build(files: FileInput[], meta: BuildMeta): Promise<BuildResult> {
  if (!meta || typeof meta.name !== "string" || meta.name.length === 0) {
    throw new BuildError(`meta.name is required (§3.3)`);
  }

  // 1. Ceilings + normalize + root inference (§3.1, §14).
  const normalized = normalize(files, meta.entry);

  // 2. Synthesize missing pieces + neutralize author SW, before hashing (§3.6, §6 step 2).
  const { files: finalFiles, entryPath, icons } = synthesize(normalized.files, normalized.entryPath, meta);

  // 3. Hash each resource to a `raw` CID; record size + content-type (§6 step 3).
  const blobs: BuiltBlob[] = [];
  for (const f of finalFiles) {
    const cid = await rawCid(f.bytes);
    blobs.push({
      path: f.path,
      cid,
      bytes: f.bytes,
      size: f.bytes.byteLength,
      contentType: contentTypeFor(f.path),
      blobMimeType: canonicalBlobMimeType(f.bytes),
    });
  }
  blobs.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));

  // 4. Assemble canonical MASL + routing, then dCBOR42 -> root CID (§6 step 4, §3.7).
  const resourcePaths = new Set<string>(["/", ...blobs.map((b) => b.path)]);
  const routing = canonicalizeRouting(meta.routing, resourcePaths);
  const manifest = assembleMasl({ meta, blobs, entryPath, icons, routing });

  const { cid: rootCid, bytes: encodedManifest } = await dagCbor(manifest);

  return {
    rootCid,
    manifest,
    blobs,
    encodedManifest,
    encodedManifestSize: encodedManifest.byteLength,
  };
}
